import { useState } from 'react';
import {
  Typography,
  TextField,
  Box,
  CircularProgress,
  Checkbox,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ControlPointRounded } from '@mui/icons-material';
import { countDecimals, secondsToDuration } from '../../utils/parsing';
import { MISSING_VALUE, LIST_DELIMITER, percentileColor } from './data-table';

const TRUNCATION_LIMIT = 18;
export const CellList = ({ cell }) => {
  let val = cell.renderValue();
  if (typeof val === 'string') val = val?.split(LIST_DELIMITER);
  return (
    <List>
      {val?.map((v) => (
        <ListItem dense key={v}>
          {v?.length > TRUNCATION_LIMIT ? (
            <Tooltip title={v}>
              <ListItemText primary={`${v.substr(0, TRUNCATION_LIMIT)}...`} />
            </Tooltip>
          ) : (
            <ListItemText primary={v} />
          )}
        </ListItem>
      ))}
    </List>
  );
};

export const DurationCell = ({ renderedCellValue }) => {
  const minutes = renderedCellValue;
  if (minutes === null || minutes === MISSING_VALUE) return null;
  return new Intl.DurationFormat('en', { style: 'short' }).format(secondsToDuration(minutes * 60));
};

export const DateCell = ({ cell }) => {
  let value = cell.getValue();
  if (value === null || value === MISSING_VALUE) return null;
  if (!Number.isNaN(value)) {
    value = new Date(value);
  }
  return (
    <Tooltip title={`Time are in ${Intl.DateTimeFormat().resolvedOptions().timeZone}`}>
      {value?.toLocaleDateString()} {value?.toLocaleTimeString()}
    </Tooltip>
  );
};

export function PrecisionCell(cell, precision) {
  const value = cell.getValue();
  const number = Number.parseFloat(value);
  if (!Number.isNaN(number) && countDecimals(number) > 2)
    return <Tooltip title={number}>{number.toFixed(precision)}...</Tooltip>;
  return value;
}

export const TooltipHeader = ({ tooltip, content }) => (
  <Tooltip title={tooltip} placement="top">
    {content}
  </Tooltip>
);

export const PredictionsChip = ({ color, predictions }) => {
  if (predictions) {
    let label = `${(predictions.min * 100.0).toPrecision(3)}%`;
    if (predictions.min !== predictions.max)
      label = `${(predictions.min * 100.0).toPrecision(3)}% - ${(predictions.max * 100.0).toPrecision(3)}%`;
    return <Chip color={color} label={label} />;
  }
  return null;
};

export const CheckboxFilter = ({ column }) => (
  <Checkbox
    sx={{ padding: '0px' }}
    color={column.getFilterValue() === 'true' ? 'success' : 'secondary'}
    indeterminate={column.getFilterValue() === undefined}
    checked={column.getFilterValue() === 'true'}
    onChange={(event) =>
      column.setFilterValue(
        column.getFilterValue() === 'false' ? undefined : event.target.checked.toString()
      )
    }
  />
);

export const PercentageCell = ({ cell }) => {
  const value = cell.getValue();
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        color={percentileColor(value)}
        value={value <= 0 || value === MISSING_VALUE ? 100 : value}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" component="div" color="text.secondary">
          {value < 0 || value === MISSING_VALUE ? '-' : `${Math.floor(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
};

export const TextEditCell = ({ value, update, validator }) => {
  const [newValue, setNewValue] = useState(value);
  const [errors, setErrors] = useState();
  const handleChange = (event) => {
    const val = event.target.value;
    const validation = !validator ? undefined : validator(val);
    setErrors(validation);
    setNewValue(val);
  };
  return (
    <TextField
      value={newValue}
      onChange={handleChange}
      onBlur={() => !errors && update(newValue)}
      error={!!errors}
      helperText={errors}
    />
  );
};

export const ListTextEditCell = ({ value, update, validator }) => {
  const [newValue, setNewValue] = useState(value);
  return (
    <>
      {newValue.map((val, idx) => (
        <TextEditCell
          key={`list-edit-ext-${val}`}
          value={val}
          validator={(v) => validator(v, idx)}
          update={(newVal) => {
            const newArray = [...newValue];
            newArray[idx] = newVal;
            setNewValue(newArray);
            update(newArray);
          }}
        />
      ))}
      <IconButton onClick={() => setNewValue([...newValue, ''])}>
        <ControlPointRounded color="success" />
      </IconButton>
    </>
  );
};

export const NumberEditCell = ({ value, label, update, validator }) => {
  const [newValue, setNewValue] = useState();
  const [validation, setValidation] = useState('');
  return (
    <TextField
      type="tel"
      label={label}
      defaultValue={value}
      onChange={({ target }) => {
        const v = validator(target.value);
        setValidation(v);
        if (!v) setNewValue(Number.parseInt(target.value, 10));
      }}
      error={!!validation}
      helperText={validation}
      onBlur={() => update(newValue)}
    />
  );
};

export const CheckboxCell = (props) => {
  const { column, row, cell } = props;
  return (
    <Checkbox
      sx={{ padding: '0px' }}
      checked={cell.getValue()}
      onChange={(event) => column.columnDef.onChange(event, row)}
    />
  );
};
